'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import {
  Zap, Play, Edit2, Trash2, ChevronDown, Plus, BarChart2,
  History, Search, RotateCcw, Eye, ChevronLeft, ChevronRight,
  SortAsc, SortDesc, RefreshCw, Settings, ExternalLink,
  CheckCircle, AlertTriangle, XCircle, Clock, Webhook,
  Server, Shield, Bell, Loader2, Database, MessageSquare,
  Save, X, Copy, Check, Mail, Send,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ───────────────────────────────────────────────────────────────────

interface WorkflowRule {
  id: string;
  name: string;
  description: string;
  trigger_type: string;
  trigger_condition: string;
  target_entity: string;
  action_template: string;
  action_command: string;
  workflow_status: string;
  priority: string;
  has_rollback: boolean;
  executions: number;
  success_rate: number;
  last_run: string;
}

interface ActionTemplate {
  id: string;
  template_type: string;
  label: string;
  description: string;
  default_command: string;
  risk_level: string;
  requires_approval: boolean;
  avg_duration: string;
  usage_count: number;
}

interface WorkflowJob {
  id: string;
  job_ref: string;
  rule_name: string;
  incident_ref: string;
  node_name: string;
  region: string;
  trigger_source: string;
  job_status: string;
  started_at: string;
  duration_label: string;
  output_log: string;
  rollback_available: boolean;
  executed_by: string;
}

interface Integration {
  id: string;
  integration_key: string;
  name: string;
  category: string;
  integration_status: string;
  endpoint: string;
  details: string;
  sync_interval: string;
  last_sync: string;
  tickets_created?: number;
  events_forwarded?: number;
  snow_instance_url?: string;
  snow_username?: string;
  snow_assignment_group?: string;
  slack_channel?: string;
  slack_webhook_url?: string;
  email_recipients?: string;
  resend_api_key?: string;
  notify_on_rule_execution?: boolean;
  notify_on_job_completion?: boolean;
  notify_on_incident_alert?: boolean;
  notify_on_sla_breach?: boolean;
  notify_on_remediation_failed?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const triggerColors: Record<string, string> = {
  ai_detection: 'text-primary bg-primary/10 border-primary/20',
  threshold: 'text-warning bg-warning/10 border-warning/20',
  manual: 'text-muted bg-muted/10 border-muted/20',
  schedule: 'text-info bg-info/10 border-info/20',
};

const triggerLabel: Record<string, string> = {
  ai_detection: 'AI Detection',
  threshold: 'Threshold',
  manual: 'Manual',
  schedule: 'Schedule',
};

const priorityDot: Record<string, string> = {
  high: 'bg-danger',
  critical: 'bg-danger',
  medium: 'bg-warning',
  low: 'bg-success',
};

const statusConfig: Record<string, { icon: React.ElementType; color: string; bg: string; border: string; label: string }> = {
  connected: { icon: CheckCircle, color: 'text-success', bg: 'bg-success/10', border: 'border-success/20', label: 'Connected' },
  degraded: { icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/20', label: 'Degraded' },
  disconnected: { icon: XCircle, color: 'text-danger', bg: 'bg-danger/10', border: 'border-danger/20', label: 'Disconnected' },
};

const jobStatusColor: Record<string, string> = {
  success: 'text-success bg-success/10 border-success/20',
  failed: 'text-danger bg-danger/10 border-danger/20',
  running: 'text-primary bg-primary/10 border-primary/20',
  queued: 'text-muted bg-muted/10 border-muted/20',
  rolled_back: 'text-warning bg-warning/10 border-warning/20',
};

const actionTemplateIcon: Record<string, React.ElementType> = {
  reboot: Server,
  isolate_node: Shield,
  notify_team: Bell,
  reroute_traffic: Zap,
  restart_service: RefreshCw,
  custom: Settings,
};

const riskColor: Record<string, string> = {
  low: 'text-success bg-success/10 border-success/20',
  medium: 'text-warning bg-warning/10 border-warning/20',
  high: 'text-danger bg-danger/10 border-danger/20',
  critical: 'text-danger bg-danger/10 border-danger/20',
};

function formatTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  } catch {
    return iso;
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-12 bg-surface-raised rounded-lg" />
      ))}
    </div>
  );
}

// ─── Trigger Rules Section ────────────────────────────────────────────────────

function TriggerRulesSection({ rules, loading, onToggle, onRefresh }: {
  rules: WorkflowRule[];
  loading: boolean;
  onToggle: (id: string, currentStatus: string) => void;
  onRefresh: () => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState('all');

  const filtered = rules.filter((r) =>
    filterType === 'all' ? true : r.trigger_type === filterType
  );

  const activeCount = rules.filter((r) => r.workflow_status === 'active').length;

  const successColor = (rate: number) =>
    rate >= 95 ? 'text-success' : rate >= 80 ? 'text-warning' : 'text-danger';

  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap size={15} className="text-primary" />
          <p className="text-sm font-semibold text-foreground">Trigger Rules</p>
          <span className="text-[10px] bg-success/10 text-success border border-success/20 px-2 py-0.5 rounded-full font-mono">
            {activeCount} active
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="text-xs bg-surface-raised border border-border rounded-lg pl-2 pr-6 py-1 text-foreground-muted focus:outline-none focus:border-primary/50 appearance-none cursor-pointer"
          >
            <option value="all">All Triggers</option>
            <option value="ai_detection">AI Detection</option>
            <option value="threshold">Threshold</option>
            <option value="manual">Manual</option>
            <option value="schedule">Schedule</option>
          </select>
          <button
            onClick={onRefresh}
            className="p-1.5 rounded hover:bg-surface-raised text-muted hover:text-foreground transition-colors"
            title="Refresh"
          >
            <RefreshCw size={12} />
          </button>
          <button
            onClick={() => {}}
            className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors"
          >
            <Plus size={12} />
            New Rule
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <div className="space-y-2 max-h-[480px] overflow-y-auto scrollbar-hide">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted">No rules found</div>
          ) : (
            filtered.map((rule) => {
              const enabled = rule.workflow_status === 'active';
              const expanded = expandedId === rule.id;
              return (
                <div
                  key={rule.id}
                  className={`border rounded-lg overflow-hidden transition-all duration-200
                    ${enabled ? 'border-border' : 'border-border/40 opacity-60'}
                    ${expanded ? 'bg-surface-raised' : 'hover:bg-surface-raised/50'}`}
                >
                  <div className="flex items-center gap-3 px-3 py-2.5">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${priorityDot[rule.priority] || 'bg-muted'}`} />
                    <button
                      onClick={() => onToggle(rule.id, rule.workflow_status)}
                      className={`relative flex-shrink-0 rounded-full transition-all duration-200 focus:outline-none`}
                      style={{ minWidth: 32, height: 18 }}
                      title={enabled ? 'Pause rule' : 'Activate rule'}
                    >
                      <span className={`absolute inset-0 rounded-full transition-colors ${enabled ? 'bg-primary' : 'bg-surface-overlay border border-border'}`} />
                      <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-all duration-200 ${enabled ? 'left-[14px]' : 'left-0.5'}`} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[12px] font-semibold text-foreground truncate">{rule.name}</p>
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border uppercase tracking-wide flex-shrink-0 ${triggerColors[rule.trigger_type] || 'text-muted bg-muted/10 border-muted/20'}`}>
                          {triggerLabel[rule.trigger_type] || rule.trigger_type}
                        </span>
                        {rule.has_rollback && (
                          <span className="text-[9px] bg-info/10 text-info border border-info/20 px-1.5 py-0.5 rounded uppercase tracking-wide font-semibold flex-shrink-0">
                            Rollback
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] flex-shrink-0">
                      <span className={`font-mono font-bold ${successColor(rule.success_rate)}`}>{rule.success_rate}%</span>
                      <span className="text-muted font-mono">{rule.executions.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => {}}
                        className="p-1 rounded hover:bg-surface-overlay text-muted hover:text-foreground transition-colors"
                        title="Edit rule"
                      >
                        <Edit2 size={11} />
                      </button>
                      <button
                        onClick={() => {}}
                        className="p-1 rounded hover:bg-surface-overlay text-muted hover:text-primary transition-colors"
                        title="Run manually"
                      >
                        <Play size={11} />
                      </button>
                      <button
                        onClick={() => setExpandedId(expanded ? null : rule.id)}
                        className="p-1 rounded hover:bg-surface-overlay text-muted hover:text-foreground transition-colors"
                      >
                        <ChevronDown size={11} className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  </div>
                  {expanded && (
                    <div className="px-3 pb-3 pt-0 border-t border-border/50 space-y-2.5">
                      <p className="text-[11px] text-foreground-muted pt-2">{rule.description}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { label: 'Trigger Condition', value: rule.trigger_condition },
                          { label: 'Target', value: rule.target_entity },
                          { label: 'Action', value: rule.action_command },
                          { label: 'Last Run', value: rule.last_run },
                        ].map((f) => (
                          <div key={f.label} className="bg-surface rounded-lg p-2">
                            <p className="text-[9px] font-semibold uppercase tracking-widest text-muted mb-0.5">{f.label}</p>
                            <p className="text-[11px] font-mono text-foreground break-all">{f.value}</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {}}
                          className="flex items-center gap-1.5 text-[11px] text-danger hover:bg-danger/10 px-2 py-1 rounded transition-colors"
                        >
                          <Trash2 size={11} />
                          Delete
                        </button>
                        <button
                          onClick={() => {}}
                          className="flex items-center gap-1.5 text-[11px] text-muted hover:text-foreground px-2 py-1 rounded hover:bg-surface-overlay transition-colors"
                        >
                          <BarChart2 size={11} />
                          History
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─── Action Templates Section ─────────────────────────────────────────────────

function ActionTemplatesSection({ templates, loading }: { templates: ActionTemplate[]; loading: boolean }) {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Settings size={15} className="text-primary" />
          <p className="text-sm font-semibold text-foreground">Action Templates</p>
          <span className="text-[10px] bg-surface-raised border border-border px-2 py-0.5 rounded-full text-muted font-mono">
            {templates.length} templates
          </span>
        </div>
      </div>

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {templates.map((tpl) => {
            const Icon = actionTemplateIcon[tpl.template_type] || Settings;
            const isSelected = selected === tpl.template_type;
            return (
              <button
                key={tpl.id}
                onClick={() => setSelected(isSelected ? null : tpl.template_type)}
                className={`text-left border rounded-xl p-3.5 transition-all duration-150 group
                  ${isSelected ? 'border-primary/40 bg-primary/5' : 'border-border hover:border-primary/30 hover:bg-surface-raised/50'}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-primary/15' : 'bg-surface-raised'}`}>
                    <Icon size={15} className={isSelected ? 'text-primary' : 'text-muted'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-[12px] font-semibold text-foreground">{tpl.label}</p>
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border uppercase tracking-wide ${riskColor[tpl.risk_level] || 'text-muted bg-muted/10 border-muted/20'}`}>
                        {tpl.risk_level}
                      </span>
                    </div>
                    <p className="text-[11px] text-foreground-muted leading-snug">{tpl.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-muted">
                      <div className="flex items-center gap-1">
                        <Clock size={9} />
                        <span>{tpl.avg_duration}</span>
                      </div>
                      <span>·</span>
                      <span className="text-primary font-semibold">{tpl.usage_count.toLocaleString()} uses</span>
                      {tpl.requires_approval && (
                        <>
                          <span>·</span>
                          <span className="text-warning font-semibold">Approval req.</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {isSelected && (
                  <div className="mt-3 pt-3 border-t border-border/50">
                    <p className="text-[9px] font-semibold uppercase tracking-widest text-muted mb-1">Default Command</p>
                    <code className="text-[10px] font-mono text-foreground bg-surface-raised px-2 py-1 rounded block break-all">
                      {tpl.default_command}
                    </code>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Email Notification Helper ────────────────────────────────────────────────

async function sendAutomationEmail(payload: {
  type: 'rule_execution' | 'job_completion' | 'incident_alert' | 'sla_breach' | 'remediation_failed';
  ruleName?: string;
  jobRef?: string;
  jobStatus?: string;
  nodeName?: string;
  region?: string;
  incidentRef?: string;
  triggerSource?: string;
  outputLog?: string;
  executedBy?: string;
  recipients?: string[];
  // incident_alert / sla_breach
  incidentTitle?: string;
  incidentSeverity?: string;
  incidentType?: string;
  aiClassification?: string;
  aiConfidence?: number;
  assignee?: string;
  customer?: string;
  slaDeadline?: string;
  // remediation_failed
  failureReason?: string;
}) {
  try {
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !supabaseKey) return { success: false, error: 'Supabase not configured' };

    const response = await fetch(`${supabaseUrl}/functions/v1/send-automation-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseKey}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return response.ok ? { success: true, ...data } : { success: false, error: data.error };
  } catch (err: unknown) {
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ─── Job History Section ──────────────────────────────────────────────────────

function JobHistorySection({ jobs, loading, onRefresh, resendRecipients }: {
  jobs: WorkflowJob[];
  loading: boolean;
  onRefresh: () => void;
  resendRecipients: string[];
}) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof WorkflowJob>('started_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState<string | null>(null);
  const perPage = 8;

  const handleSendJobEmail = async (job: WorkflowJob) => {
    setSendingEmail(job.id);
    const result = await sendAutomationEmail({
      type: 'job_completion',
      ruleName: job.rule_name,
      jobRef: job.job_ref,
      jobStatus: job.job_status,
      nodeName: job.node_name,
      region: job.region,
      incidentRef: job.incident_ref,
      triggerSource: job.trigger_source,
      outputLog: job.output_log,
      executedBy: job.executed_by,
      recipients: resendRecipients,
    });
    setSendingEmail(null);
    if (result.success) {
      setEmailSent(job.id);
      setTimeout(() => setEmailSent(null), 3000);
    }
  };

  const filtered = jobs
    .filter((j) => {
      const q = search.toLowerCase();
      const matchQ = !q || j.job_ref.toLowerCase().includes(q) || j.rule_name.toLowerCase().includes(q) || j.node_name.toLowerCase().includes(q) || j.incident_ref.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || j.job_status === statusFilter;
      return matchQ && matchStatus;
    })
    .sort((a, b) => {
      const av = String(a[sortField] ?? '');
      const bv = String(b[sortField] ?? '');
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (field: keyof WorkflowJob) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ col }: { col: keyof WorkflowJob }) => {
    if (sortField !== col) return <SortAsc size={10} className="opacity-30 text-muted" />;
    return sortDir === 'asc' ? <SortAsc size={10} className="text-primary" /> : <SortDesc size={10} className="text-primary" />;
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History size={15} className="text-primary" />
          <p className="text-sm font-semibold text-foreground">Job History</p>
          <span className="text-[10px] bg-surface-raised border border-border px-2 py-0.5 rounded-full text-muted font-mono">
            {jobs.length} jobs
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
            <input
              type="text"
              placeholder="Search jobs…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-7 pr-3 py-1.5 text-xs bg-surface-raised border border-border rounded-lg text-foreground placeholder:text-muted focus:outline-none focus:border-primary/50 w-44 transition-colors"
            />
          </div>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="text-xs bg-surface-raised border border-border rounded-lg pl-3 pr-7 py-1.5 text-foreground-muted focus:outline-none focus:border-primary/50 appearance-none cursor-pointer"
            >
              <option value="all">All Statuses</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="running">Running</option>
              <option value="queued">Queued</option>
              <option value="rolled_back">Rolled Back</option>
            </select>
            <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          </div>
          <button onClick={onRefresh} className="p-1.5 rounded hover:bg-surface-raised text-muted hover:text-foreground transition-colors" title="Refresh">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  {[
                    { key: 'job_ref' as keyof WorkflowJob, label: 'Job ID' },
                    { key: 'rule_name' as keyof WorkflowJob, label: 'Rule' },
                    { key: null, label: 'Incident' },
                    { key: 'node_name' as keyof WorkflowJob, label: 'Node' },
                    { key: null, label: 'Region' },
                    { key: null, label: 'Triggered By' },
                    { key: 'job_status' as keyof WorkflowJob, label: 'Status' },
                    { key: 'started_at' as keyof WorkflowJob, label: 'Started' },
                    { key: 'duration_label' as keyof WorkflowJob, label: 'Duration' },
                    { key: null, label: 'Rollback' },
                    { key: null, label: '' },
                  ].map(({ key, label }, i) => (
                    <th
                      key={`th-${i}`}
                      onClick={key ? () => toggleSort(key) : undefined}
                      className={`text-left pb-2 pr-3 text-[10px] font-semibold uppercase tracking-widest text-muted whitespace-nowrap
                        ${key ? 'cursor-pointer hover:text-foreground transition-colors select-none' : ''}`}
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        {key && <SortIcon col={key} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <History size={28} className="text-muted" />
                        <p className="text-sm text-foreground-muted">No jobs match your filters</p>
                        <button onClick={() => { setSearch(''); setStatusFilter('all'); }} className="text-xs text-primary hover:underline">
                          Clear filters
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginated.map((job, idx) => (
                    <React.Fragment key={job.id}>
                      <tr className={`border-b border-border/40 transition-colors group ${idx % 2 === 1 ? 'bg-surface-raised/20' : ''} hover:bg-surface-raised/50`}>
                        <td className="py-2.5 pr-3 font-mono text-[11px] text-primary font-semibold whitespace-nowrap">{job.job_ref}</td>
                        <td className="py-2.5 pr-3 font-medium text-foreground whitespace-nowrap">{job.rule_name}</td>
                        <td className="py-2.5 pr-3 font-mono text-[11px] text-info whitespace-nowrap">{job.incident_ref}</td>
                        <td className="py-2.5 pr-3 font-mono text-[11px] text-foreground whitespace-nowrap">{job.node_name}</td>
                        <td className="py-2.5 pr-3 text-foreground-muted whitespace-nowrap">{job.region}</td>
                        <td className="py-2.5 pr-3">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border uppercase tracking-wide
                            ${job.trigger_source === 'AI Detection' ? 'text-primary bg-primary/10 border-primary/20' : 'text-warning bg-warning/10 border-warning/20'}`}>
                            {job.trigger_source}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border uppercase tracking-wide ${jobStatusColor[job.job_status] || 'text-muted bg-muted/10 border-muted/20'}`}>
                            {job.job_status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3 font-mono text-[11px] text-foreground-muted whitespace-nowrap">{formatTime(job.started_at)}</td>
                        <td className="py-2.5 pr-3 font-mono text-[11px] text-foreground-muted whitespace-nowrap">{job.duration_label}</td>
                        <td className="py-2.5 pr-3">
                          {job.rollback_available ? (
                            <button className="flex items-center gap-1 text-[10px] text-warning hover:text-warning/80 transition-colors">
                              <RotateCcw size={10} />
                              Rollback
                            </button>
                          ) : (
                            <span className="text-[10px] text-muted">N/A</span>
                          )}
                        </td>
                        <td className="py-2.5">
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => setExpandedId(expandedId === job.id ? null : job.id)}
                              className="p-1 rounded hover:bg-surface-overlay text-muted hover:text-foreground transition-colors"
                              title="View output"
                            >
                              <Eye size={12} />
                            </button>
                            {resendRecipients.length > 0 && (
                              <button
                                onClick={() => handleSendJobEmail(job)}
                                disabled={sendingEmail === job.id}
                                className="p-1 rounded hover:bg-surface-overlay transition-colors"
                                title="Send email notification"
                              >
                                {sendingEmail === job.id ? (
                                  <Loader2 size={12} className="animate-spin text-primary" />
                                ) : emailSent === job.id ? (
                                  <Check size={12} className="text-success" />
                                ) : (
                                  <Mail size={12} className="text-muted hover:text-primary" />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedId === job.id && (
                        <tr key={`${job.id}-exp`} className="bg-surface-raised/30">
                          <td colSpan={11} className="px-4 py-3">
                            <div className="flex items-start gap-2">
                              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted mt-0.5 flex-shrink-0">Output:</span>
                              <code className={`text-[11px] font-mono leading-relaxed
                                ${job.job_status === 'failed' ? 'text-danger' : job.job_status === 'rolled_back' ? 'text-warning' : 'text-foreground-muted'}`}>
                                {job.output_log}
                              </code>
                            </div>
                            <div className="flex items-center gap-3 mt-2 text-[10px] text-muted">
                              <span>Executed by: <span className="text-foreground-muted font-mono">{job.executed_by}</span></span>
                              <span>·</span>
                              <span>Node: <span className="text-foreground-muted font-mono">{job.node_name}</span></span>
                              {resendRecipients.length > 0 && (
                                <>
                                  <span>·</span>
                                  <button
                                    onClick={() => handleSendJobEmail(job)}
                                    disabled={sendingEmail === job.id}
                                    className="flex items-center gap-1 text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
                                  >
                                    {sendingEmail === job.id ? (
                                      <Loader2 size={10} className="animate-spin" />
                                    ) : emailSent === job.id ? (
                                      <Check size={10} className="text-success" />
                                    ) : (
                                      <Send size={10} />
                                    )}
                                    {emailSent === job.id ? 'Email sent!' : 'Notify team'}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
            <span className="text-[11px] text-muted">
              {filtered.length} job{filtered.length !== 1 ? 's' : ''} · Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded text-muted hover:text-foreground hover:bg-surface-raised disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft size={13} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pg = i + 1;
                return (
                  <button
                    key={pg}
                    onClick={() => setPage(pg)}
                    className={`w-6 h-6 rounded text-[11px] font-mono transition-all
                      ${page === pg ? 'bg-primary text-white' : 'text-muted hover:text-foreground hover:bg-surface-raised'}`}
                  >
                    {pg}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded text-muted hover:text-foreground hover:bg-surface-raised disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Integration Settings Section ─────────────────────────────────────────────

function IntegrationSettingsSection({ integrations, loading, onSave }: {
  integrations: Integration[];
  loading: boolean;
  onSave: (id: string, updates: Partial<Integration>) => Promise<void>;
}) {
  const [activeIntg, setActiveIntg] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState<{ success: boolean; message: string } | null>(null);
  const [formData, setFormData] = useState<Record<string, Partial<Integration>>>({});

  const handleSync = (id: string, name: string) => {
    setSyncing(id);
    setTimeout(() => { setSyncing(null); }, 1500);
  };

  const handleSave = async (intg: Integration) => {
    setSaving(intg.id);
    await onSave(intg.id, formData[intg.id] || {});
    setSaving(null);
    setActiveIntg(null);
  };

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const updateField = (intgId: string, field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [intgId]: { ...(prev[intgId] || {}), [field]: value },
    }));
  };

  const handleTestEmail = async (intg: Integration) => {
    setTestingEmail(true);
    setTestEmailResult(null);
    const form = formData[intg.id] || {};
    const recipients = ((form.email_recipients ?? intg.email_recipients) || '')
      .split(',').map((e: string) => e.trim()).filter(Boolean);

    const result = await sendAutomationEmail({
      type: 'job_completion',
      ruleName: 'Test Rule — BGP-Session-Restart',
      jobRef: 'WF-TEST',
      jobStatus: 'success',
      nodeName: 'CORE-ADD-01',
      region: 'Arada',
      incidentRef: 'INC-TEST',
      triggerSource: 'Manual',
      outputLog: 'Test notification from NetOpsAI Automation Workflows. Email integration is working correctly.',
      executedBy: 'netopsai-engine',
      recipients,
    });
    setTestingEmail(false);
    setTestEmailResult({
      success: result.success,
      message: result.success ? `Test email sent to ${recipients.join(', ')}` : (result.error || 'Failed to send test email'),
    });
    setTimeout(() => setTestEmailResult(null), 5000);
  };

  const intgIcon: Record<string, React.ElementType> = {
    servicenow: Database,
    slack: MessageSquare,
    resend: Mail,
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Webhook size={15} className="text-primary" />
          <p className="text-sm font-semibold text-foreground">Integration Settings</p>
          <span className="text-[10px] bg-surface-raised border border-border px-2 py-0.5 rounded-full text-muted font-mono">
            {integrations.length} configured
          </span>
        </div>
      </div>

      {loading ? (
        <LoadingSkeleton />
      ) : (
        <div className="space-y-4">
          {integrations.map((intg) => {
            const cfg = statusConfig[intg.integration_status] || statusConfig.disconnected;
            const StatusIcon = cfg.icon;
            const IntgIcon = intgIcon[intg.integration_key] || Webhook;
            const isSyncing = syncing === intg.id;
            const isSaving = saving === intg.id;
            const isExpanded = activeIntg === intg.id;
            const form = formData[intg.id] || {};

            return (
              <div
                key={intg.id}
                className={`border rounded-xl transition-all duration-200
                  ${intg.integration_status === 'disconnected' ? 'border-danger/20 bg-danger/5' :
                    intg.integration_status === 'degraded' ? 'border-warning/20 bg-warning/5' : 'border-border'}`}
              >
                {/* Header */}
                <div className="flex items-start gap-3 p-4">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                    <IntgIcon size={16} className={cfg.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-[13px] font-semibold text-foreground">{intg.name}</p>
                      <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                        {cfg.label}
                      </span>
                      <span className="text-[9px] bg-surface-overlay text-muted px-1.5 py-0.5 rounded border border-border uppercase tracking-wide font-semibold">
                        {intg.category}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted font-mono truncate">{intg.endpoint}</p>
                    <p className="text-[11px] text-foreground-muted mt-0.5">{intg.details}</p>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-muted">
                      <div className="flex items-center gap-1">
                        <Clock size={9} />
                        <span>Sync: {intg.sync_interval}</span>
                      </div>
                      <span>·</span>
                      <span>Last: {intg.last_sync}</span>
                      {intg.tickets_created != null && (
                        <><span>·</span><span className="text-primary font-semibold">{intg.tickets_created.toLocaleString()} tickets</span></>
                      )}
                      {intg.events_forwarded != null && (
                        <><span>·</span><span className="text-primary font-semibold">{intg.events_forwarded.toLocaleString()} events</span></>
                      )}
                      {intg.integration_key === 'resend' && intg.email_recipients && (
                        <><span>·</span><span className="text-primary font-semibold font-mono truncate max-w-[160px]">{intg.email_recipients}</span></>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {intg.integration_key !== 'resend' && (
                      <button
                        onClick={() => handleSync(intg.id, intg.name)}
                        className="p-1.5 rounded hover:bg-surface-overlay text-muted hover:text-foreground transition-colors"
                        title="Force sync"
                        disabled={isSyncing}
                      >
                        <RefreshCw size={12} className={isSyncing ? 'animate-spin text-primary' : ''} />
                      </button>
                    )}
                    <button
                      onClick={() => setActiveIntg(isExpanded ? null : intg.id)}
                      className="p-1.5 rounded hover:bg-surface-overlay text-muted hover:text-foreground transition-colors"
                      title="Configure"
                    >
                      <Settings size={12} />
                    </button>
                    {intg.integration_key !== 'resend' && (
                      <button
                        onClick={() => {}}
                        className="p-1.5 rounded hover:bg-surface-overlay text-muted hover:text-primary transition-colors"
                        title="Open portal"
                      >
                        <ExternalLink size={12} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Settings Panel */}
                {isExpanded && (
                  <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
                    <p className="text-[11px] font-semibold text-foreground-muted uppercase tracking-widest">
                      {intg.name} Configuration
                    </p>

                    {intg.integration_key === 'servicenow' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { label: 'Instance URL', field: 'snow_instance_url', placeholder: 'https://your-instance.service-now.com', value: form.snow_instance_url ?? intg.snow_instance_url ?? '' },
                          { label: 'Username', field: 'snow_username', placeholder: 'netopsai-svc', value: form.snow_username ?? intg.snow_username ?? '' },
                          { label: 'API Token', field: 'snow_api_token', placeholder: '••••••••••••••••', value: (form as Record<string, string>).snow_api_token ?? '' },
                          { label: 'Assignment Group', field: 'snow_assignment_group', placeholder: 'Network Operations', value: form.snow_assignment_group ?? intg.snow_assignment_group ?? '' },
                        ].map((f) => (
                          <div key={f.field}>
                            <label className="text-[10px] font-semibold uppercase tracking-widest text-muted block mb-1">{f.label}</label>
                            <div className="relative">
                              <input
                                type={f.field === 'snow_api_token' ? 'password' : 'text'}
                                value={f.value}
                                onChange={(e) => updateField(intg.id, f.field, e.target.value)}
                                placeholder={f.placeholder}
                                className="w-full text-xs bg-surface-raised border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted focus:outline-none focus:border-primary/50 transition-colors pr-8"
                              />
                              {f.value && (
                                <button
                                  onClick={() => handleCopy(f.value, `${intg.id}-${f.field}`)}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-foreground transition-colors"
                                >
                                  {copied === `${intg.id}-${f.field}` ? <Check size={11} className="text-success" /> : <Copy size={11} />}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {intg.integration_key === 'slack' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { label: 'Webhook URL', field: 'slack_webhook_url', placeholder: 'https://hooks.slack.com/services/T0…', value: form.slack_webhook_url ?? intg.slack_webhook_url ?? '' },
                          { label: 'Alert Channel', field: 'slack_channel', placeholder: '#netops-alerts', value: form.slack_channel ?? intg.slack_channel ?? '' },
                          { label: 'Bot Token', field: 'slack_bot_token', placeholder: 'xoxb-••••••••••••', value: (form as Record<string, string>).slack_bot_token ?? '' },
                          { label: 'Signing Secret', field: 'slack_signing_secret', placeholder: '••••••••••••••••', value: (form as Record<string, string>).slack_signing_secret ?? '' },
                        ].map((f) => (
                          <div key={f.field}>
                            <label className="text-[10px] font-semibold uppercase tracking-widest text-muted block mb-1">{f.label}</label>
                            <div className="relative">
                              <input
                                type={f.field.includes('token') || f.field.includes('secret') ? 'password' : 'text'}
                                value={f.value}
                                onChange={(e) => updateField(intg.id, f.field, e.target.value)}
                                placeholder={f.placeholder}
                                className="w-full text-xs bg-surface-raised border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted focus:outline-none focus:border-primary/50 transition-colors pr-8"
                              />
                              {f.value && (
                                <button
                                  onClick={() => handleCopy(f.value, `${intg.id}-${f.field}`)}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-foreground transition-colors"
                                >
                                  {copied === `${intg.id}-${f.field}` ? <Check size={11} className="text-success" /> : <Copy size={11} />}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {intg.integration_key === 'resend' && (
                      <div className="space-y-3">
                        {/* Info banner */}
                        <div className="flex items-start gap-2 bg-primary/5 border border-primary/20 rounded-lg p-3">
                          <Mail size={13} className="text-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-[11px] font-semibold text-primary mb-0.5">Resend Email via Supabase Edge Function</p>
                            <p className="text-[11px] text-foreground-muted leading-relaxed">
                              Add your <span className="font-mono text-primary">RESEND_API_KEY</span> to the Supabase Edge Function secrets, then configure recipients below. Free tier: 3,000 emails/month.
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="sm:col-span-2">
                            <label className="text-[10px] font-semibold uppercase tracking-widest text-muted block mb-1">
                              Email Recipients <span className="text-muted font-normal normal-case">(comma-separated)</span>
                            </label>
                            <input
                              type="text"
                              value={form.email_recipients ?? intg.email_recipients ?? ''}
                              onChange={(e) => updateField(intg.id, 'email_recipients', e.target.value)}
                              placeholder="ops@company.com, oncall@company.com"
                              className="w-full text-xs bg-surface-raised border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted focus:outline-none focus:border-primary/50 transition-colors"
                            />
                          </div>
                        </div>

                        {/* Notification toggles */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                            { label: 'Notify on Rule Execution', field: 'notify_on_rule_execution', value: form.notify_on_rule_execution ?? intg.notify_on_rule_execution ?? true },
                            { label: 'Notify on Job Completion', field: 'notify_on_job_completion', value: form.notify_on_job_completion ?? intg.notify_on_job_completion ?? true },
                            { label: 'Notify on New Incident (P1/P2)', field: 'notify_on_incident_alert', value: form.notify_on_incident_alert ?? intg.notify_on_incident_alert ?? false },
                            { label: 'Notify on SLA Breach', field: 'notify_on_sla_breach', value: form.notify_on_sla_breach ?? intg.notify_on_sla_breach ?? false },
                            { label: 'Notify on Remediation Failure', field: 'notify_on_remediation_failed', value: form.notify_on_remediation_failed ?? intg.notify_on_remediation_failed ?? true },
                          ].map((toggle) => (
                            <div key={toggle.field} className="flex items-center justify-between bg-surface-raised border border-border rounded-lg px-3 py-2.5">
                              <span className="text-[11px] text-foreground-muted">{toggle.label}</span>
                              <button
                                onClick={() => updateField(intg.id, toggle.field, !toggle.value)}
                                className={`relative flex-shrink-0 rounded-full transition-all duration-200 focus:outline-none`}
                                style={{ minWidth: 32, height: 18 }}
                              >
                                <span className={`absolute inset-0 rounded-full transition-colors ${toggle.value ? 'bg-primary' : 'bg-surface-overlay border border-border'}`} />
                                <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-all duration-200 ${toggle.value ? 'left-[14px]' : 'left-0.5'}`} />
                              </button>
                            </div>
                          ))}
                        </div>

                        {/* Test email result */}
                        {testEmailResult && (
                          <div className={`flex items-center gap-2 text-[11px] px-3 py-2 rounded-lg border ${testEmailResult.success ? 'bg-success/10 border-success/20 text-success' : 'bg-danger/10 border-danger/20 text-danger'}`}>
                            {testEmailResult.success ? <Check size={12} /> : <XCircle size={12} />}
                            {testEmailResult.message}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={() => handleSave(intg)}
                        disabled={isSaving}
                        className="flex items-center gap-1.5 text-xs bg-primary/15 text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/25 transition-colors font-medium disabled:opacity-50"
                      >
                        {isSaving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                        {isSaving ? 'Saving…' : 'Save Settings'}
                      </button>
                      {intg.integration_key === 'resend' && (
                        <button
                          onClick={() => handleTestEmail(intg)}
                          disabled={testingEmail}
                          className="flex items-center gap-1.5 text-xs bg-surface-raised text-foreground-muted border border-border rounded-lg px-3 py-1.5 hover:bg-surface-overlay hover:text-foreground transition-colors disabled:opacity-50"
                        >
                          {testingEmail ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                          {testingEmail ? 'Sending…' : 'Send Test Email'}
                        </button>
                      )}
                      <button
                        onClick={() => setActiveIntg(null)}
                        className="flex items-center gap-1.5 text-xs text-muted hover:text-foreground border border-border rounded-lg px-3 py-1.5 hover:bg-surface-raised transition-colors"
                      >
                        <X size={11} />
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AutomationWorkflowsClient() {
  const supabase = createClient();

  const [rules, setRules] = useState<WorkflowRule[]>([]);
  const [templates, setTemplates] = useState<ActionTemplate[]>([]);
  const [jobs, setJobs] = useState<WorkflowJob[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);

  const [loadingRules, setLoadingRules] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);

  const [activeTab, setActiveTab] = useState<'rules' | 'templates' | 'history' | 'integrations'>('rules');

  // Derive Resend recipients from integrations for use in job history
  const resendIntegration = integrations.find((i) => i.integration_key === 'resend');
  const resendRecipients = resendIntegration?.email_recipients
    ? resendIntegration.email_recipients.split(',').map((e) => e.trim()).filter(Boolean)
    : [];

  const fetchRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      const { data, error } = await supabase
        .from('workflow_rules')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error && data) setRules(data as WorkflowRule[]);
    } catch {}
    setLoadingRules(false);
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const { data, error } = await supabase
        .from('action_templates')
        .select('*')
        .order('usage_count', { ascending: false });
      if (!error && data) setTemplates(data as ActionTemplate[]);
    } catch {}
    setLoadingTemplates(false);
  }, []);

  const fetchJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const { data, error } = await supabase
        .from('workflow_jobs')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(50);
      if (!error && data) setJobs(data as WorkflowJob[]);
    } catch {}
    setLoadingJobs(false);
  }, []);

  const fetchIntegrations = useCallback(async () => {
    setLoadingIntegrations(true);
    try {
      const { data, error } = await supabase
        .from('workflow_integrations')
        .select('*')
        .order('created_at', { ascending: true });
      if (!error && data) setIntegrations(data as Integration[]);
    } catch {}
    setLoadingIntegrations(false);
  }, []);

  useEffect(() => {
    fetchRules();
    fetchTemplates();
    fetchJobs();
    fetchIntegrations();
  }, [fetchRules, fetchTemplates, fetchJobs, fetchIntegrations]);

  // Real-time subscriptions — auto-send email on job status change to success/failed/rolled_back
  useEffect(() => {
    const jobsChannel = supabase
      .channel('workflow_jobs_rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workflow_jobs' }, (payload) => {
        fetchJobs();
        // Auto-notify on terminal job status if Resend is configured
        const newJob = payload.new as WorkflowJob | undefined;
        const notifyStatuses = ['success', 'failed', 'rolled_back'];
        if (
          newJob &&
          notifyStatuses.includes(newJob.job_status) &&
          resendIntegration?.notify_on_job_completion &&
          resendRecipients.length > 0
        ) {
          sendAutomationEmail({
            type: 'job_completion',
            ruleName: newJob.rule_name,
            jobRef: newJob.job_ref,
            jobStatus: newJob.job_status,
            nodeName: newJob.node_name,
            region: newJob.region,
            incidentRef: newJob.incident_ref,
            triggerSource: newJob.trigger_source,
            outputLog: newJob.output_log,
            executedBy: newJob.executed_by,
            recipients: resendRecipients,
          });
        }
        // Auto-notify on failed job with no rollback if configured
        if (
          newJob &&
          newJob.job_status === 'failed' &&
          !newJob.rollback_available &&
          resendIntegration?.notify_on_remediation_failed &&
          resendRecipients.length > 0
        ) {
          sendAutomationEmail({
            type: 'remediation_failed',
            ruleName: newJob.rule_name,
            jobRef: newJob.job_ref,
            jobStatus: newJob.job_status,
            nodeName: newJob.node_name,
            region: newJob.region,
            incidentRef: newJob.incident_ref,
            triggerSource: newJob.trigger_source,
            outputLog: newJob.output_log,
            executedBy: newJob.executed_by,
            recipients: resendRecipients,
          });
        }
      })
      .subscribe();

    const rulesChannel = supabase
      .channel('workflow_rules_rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workflow_rules' }, () => {
        fetchRules();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(jobsChannel);
      supabase.removeChannel(rulesChannel);
    };
  }, [fetchJobs, fetchRules, resendIntegration, resendRecipients]);

  const handleToggleRule = async (id: string, currentStatus: string) => {
    const nextStatus = currentStatus === 'active' ? 'paused' : 'active';
    setRules((prev) => prev.map((r) => r.id === id ? { ...r, workflow_status: nextStatus } : r));
    try {
      await supabase
        .from('workflow_rules')
        .update({ workflow_status: nextStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      // Notify team when a rule is activated (rule_execution event)
      if (nextStatus === 'active' && resendIntegration?.notify_on_rule_execution && resendRecipients.length > 0) {
        const rule = rules.find((r) => r.id === id);
        if (rule) {
          sendAutomationEmail({
            type: 'rule_execution',
            ruleName: rule.name,
            jobRef: `RULE-${rule.id.slice(0, 8).toUpperCase()}`,
            jobStatus: 'running',
            nodeName: rule.target_entity,
            region: 'All Regions',
            incidentRef: 'N/A',
            triggerSource: 'Manual Activation',
            outputLog: `Rule "${rule.name}" activated manually. Trigger: ${rule.trigger_condition}`,
            executedBy: 'operator',
            recipients: resendRecipients,
          });
        }
      }
    } catch {
      setRules((prev) => prev.map((r) => r.id === id ? { ...r, workflow_status: currentStatus } : r));
    }
  };

  const handleSaveIntegration = async (id: string, updates: Partial<Integration>) => {
    try {
      await supabase
        .from('workflow_integrations')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      await fetchIntegrations();
    } catch {}
  };

  const activeRules = rules.filter((r) => r.workflow_status === 'active').length;
  const successJobs = jobs.filter((j) => j.job_status === 'success').length;
  const failedJobs = jobs.filter((j) => j.job_status === 'failed').length;
  const runningJobs = jobs.filter((j) => j.job_status === 'running').length;

  const tabs = [
    { id: 'rules' as const, label: 'Trigger Rules', count: rules.length },
    { id: 'templates' as const, label: 'Action Templates', count: templates.length },
    { id: 'history' as const, label: 'Job History', count: jobs.length },
    { id: 'integrations' as const, label: 'Integrations', count: integrations.length },
  ];

  return (
    <div className="p-6 xl:p-8 max-w-screen-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Automation Workflows</h1>
          <p className="text-sm text-muted mt-0.5">
            Trigger rules · Action templates · Job history · ServiceNow, Slack & Resend integrations
          </p>
        </div>
        <button className="text-sm bg-primary/15 text-primary border border-primary/30 rounded-lg px-4 py-2 hover:bg-primary/25 transition-colors font-medium active:scale-95 flex items-center gap-2">
          <Plus size={14} />
          New Workflow
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Active Rules', value: String(activeRules), sub: `${rules.length - activeRules} paused`, color: 'text-success', bg: 'bg-success/8', border: 'border-success/20' },
          { label: 'Jobs Running', value: String(runningJobs), sub: 'In progress now', color: 'text-primary', bg: 'bg-primary/8', border: 'border-primary/20' },
          { label: 'Success (Today)', value: String(successJobs), sub: `${failedJobs} failed`, color: 'text-success', bg: 'bg-success/8', border: 'border-success/20' },
          { label: 'Action Templates', value: String(templates.length), sub: 'Available actions', color: 'text-warning', bg: 'bg-warning/8', border: 'border-warning/20' },
        ].map((s, i) => (
          <div key={i} className={`${s.bg} border ${s.border} rounded-xl p-4`}>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted">{s.label}</p>
            <p className={`text-2xl font-bold font-mono ${s.color} mt-0.5`}>{s.value}</p>
            <p className="text-[11px] text-muted mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px
              ${activeTab === tab.id
                ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-foreground hover:border-border'}`}
          >
            {tab.label}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono
              ${activeTab === tab.id ? 'bg-primary/15 text-primary' : 'bg-surface-raised text-muted'}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'rules' && (
        <TriggerRulesSection
          rules={rules}
          loading={loadingRules}
          onToggle={handleToggleRule}
          onRefresh={fetchRules}
        />
      )}
      {activeTab === 'templates' && (
        <ActionTemplatesSection templates={templates} loading={loadingTemplates} />
      )}
      {activeTab === 'history' && (
        <JobHistorySection jobs={jobs} loading={loadingJobs} onRefresh={fetchJobs} resendRecipients={resendRecipients} />
      )}
      {activeTab === 'integrations' && (
        <IntegrationSettingsSection
          integrations={integrations}
          loading={loadingIntegrations}
          onSave={handleSaveIntegration}
        />
      )}
    </div>
  );
}
